/**
 * DeviceMapMarker — a single device's marker on the fleet map.
 *
 * Visual encoding (locked with the user, see plan):
 *  - Fill colour = status (online green / offline gray / stale amber)
 *  - Ring colour = env-group (dev blue / demo purple / prod teal / unassigned slate)
 *  - Group ring is omitted entirely when the env-group is unknown
 *    (tenant-admins receive 403 on /admin/device-groups and so have no
 *    group data — markers render with a thin neutral border instead).
 *
 * Popup interior mirrors the System Status timeline's hover tooltip
 * (DeviceStateTimeline.tsx:1307–1363) and the per-row pattern from
 * DeviceStatus.tsx:66–72 — `.card`-style surface, text-xs, tabular-nums,
 * label/value rows.
 */
import { useMemo } from 'react';
import { divIcon } from 'leaflet';
import type { DivIcon, Marker as LeafletMarker } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import {
  ENV_GROUP_LABEL,
  type DeviceSummary,
  type EnvGroup,
} from '../api/hooks';
import { fmtAgo } from './format';

/** Status taxonomy for the marker fill. Mirrors the timeline palette. */
export type MarkerStatus = 'online' | 'offline' | 'stale';

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 h

/** Decide which status bucket a device falls into. */
export function deviceStatus(device: DeviceSummary): MarkerStatus {
  if (device.online) return 'online';
  if (!device.last_seen) return 'stale';
  const seen = new Date(device.last_seen).getTime();
  if (!Number.isFinite(seen)) return 'stale';
  return Date.now() - seen >= STALE_THRESHOLD_MS ? 'stale' : 'offline';
}

/**
 * Stable display order shared by the sidebar and the cluster-marker
 * popup: online devices first, offline (including stale) second; within
 * each bucket, alphabetical by name (friendly_name preferred, thing as
 * fallback, thing as tiebreaker). Locale-aware compare so Finnish
 * characters (ä, ö) behave for venue names like "Vierumäki".
 *
 * The point of this ordering is that the only re-shuffle the user sees
 * is a real online↔offline transition — not last_seen ticks from the
 * 30 s React Query poll.
 */
export function compareForSidebar(a: DeviceSummary, b: DeviceSummary): number {
  // Boolean sort: online=true → 0, false → 1.
  const onlineDelta = (a.online ? 0 : 1) - (b.online ? 0 : 1);
  if (onlineDelta !== 0) return onlineDelta;
  const aName = (a.friendly_name ?? a.thing).toLowerCase();
  const bName = (b.friendly_name ?? b.thing).toLowerCase();
  const nameDelta = aName.localeCompare(bName);
  if (nameDelta !== 0) return nameDelta;
  return a.thing.localeCompare(b.thing);
}

// Status fill colours pulled from tailwind.config.js. Keeping them as
// literals here (rather than reading the CSS variable) lets the SVG
// inside the DivIcon use them directly without a runtime style query.
// Exported so DeviceClusterMarker can pick a dominant-status fill
// without re-declaring the palette.
export const STATUS_FILL: Record<MarkerStatus, string> = {
  online: '#00B4D0',  // accent (cyan)
  offline: '#49454F', // muted
  stale: '#A8541F',   // warn
};

// Env-group ring colours. Chosen to be visually distinct from the
// status fills (no green/amber/gray collision) and from each other.
export const GROUP_RING: Record<EnvGroup, string> = {
  'artomeui-dev': '#3b82f6',         // blue-500
  'artomeui-demo': '#a855f7',        // purple-500
  'artomeui-prod': '#0d9488',        // teal-700 (also used by timeline boot ticks)
  'artomeui-unassigned': '#94a3b8',  // slate-400
};

// Fallback ring when env-group is unknown (tenant-admin scope) OR when
// a cluster contains devices from mixed env-groups. Uses the design
// system's `border` token for a calm, neutral outline.
export const FALLBACK_RING = '#E4E4E4';

/**
 * Build a DivIcon for a (status, group) combo. Memoising the SVG string
 * keeps Leaflet from allocating a new icon on every poll cycle — at
 * fleet scale the cost is small, but it also keeps the underlying DOM
 * stable, which prevents popup flicker when React Query re-renders.
 *
 * Layered composition (back-to-front, 24×24 viewport, all centred at
 * (12,12) unless noted):
 *   1. feDropShadow filter (dy=1, σ=0.8, fg @ 28 %) — lifts the marker
 *      off the tile content without imitating Google-Maps pin chrome.
 *   2. r=10.5 white disc + 0.5 px fg-tinted hairline — keeps the marker
 *      readable over light AND dark tile patches and crisply defines
 *      its edge.
 *   3. r=9 env-group ring at 2 px (was 2.5) — slimmer reads as more
 *      precise; matches the 2 px stroke weight used by the timeline's
 *      brush selection border.
 *   4. r=5 status fill — the actionable bit, dominant.
 *   5. Inner highlight: a small r=1.2 white circle at (12,10), 38 %
 *      opacity — turns the flat dot into a glass bead. Subtle enough
 *      that the eye reads it as material, not as a separate element.
 *
 * Filter IDs are suffixed with the variant key so multiple inline
 * SVGs in the same document don't collide on the global `id` lookup.
 */
export function buildMarkerIcon(
  status: MarkerStatus,
  group: EnvGroup | null,
): DivIcon {
  const fill = STATUS_FILL[status];
  const ring = group ? GROUP_RING[group] : FALLBACK_RING;
  const variantId = `${status}-${group ?? 'none'}`;
  const html = `
    <svg viewBox="0 0 24 24" width="24" height="24" overflow="visible"
         xmlns="http://www.w3.org/2000/svg"
         aria-hidden="true" focusable="false">
      <defs>
        <filter id="m-ds-${variantId}" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="0.8"
                        flood-color="#1C1B1F" flood-opacity="0.28"/>
        </filter>
      </defs>
      <g filter="url(#m-ds-${variantId})">
        <circle cx="12" cy="12" r="10.5" fill="#ffffff"/>
        <circle cx="12" cy="12" r="10.5" fill="none"
                stroke="#1C1B1F" stroke-opacity="0.15" stroke-width="0.5"/>
        <circle cx="12" cy="12" r="9" fill="none"
                stroke="${ring}" stroke-width="2"/>
        <circle cx="12" cy="12" r="5" fill="${fill}"/>
        <circle cx="12" cy="10" r="1.2" fill="#ffffff" opacity="0.38"/>
      </g>
    </svg>
  `;
  return divIcon({
    html,
    className: 'artomeui-device-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
}

interface DeviceMapMarkerProps {
  device: DeviceSummary;
  group: EnvGroup | null;
  /** Optional ref-setter so the parent can call `marker.openPopup()`. */
  registerRef?: (thing: string, marker: LeafletMarker | null) => void;
}

export function DeviceMapMarker({ device, group, registerRef }: DeviceMapMarkerProps) {
  const status = deviceStatus(device);
  const icon = useMemo(() => buildMarkerIcon(status, group), [status, group]);

  // Defensive: guard against bad geo. Caller already filters, but a
  // partial geo (only `last_scan_request`, no lat/lon) — the dead-zone
  // shape that bit DeviceLocationCard — would still slip through a
  // `geo != null` test.
  const geo = device.geo;
  if (
    !geo ||
    typeof geo.lat !== 'number' ||
    typeof geo.lon !== 'number' ||
    !Number.isFinite(geo.lat) ||
    !Number.isFinite(geo.lon)
  ) {
    return null;
  }

  const statusPillClass =
    status === 'online' ? 'pill-accent' :
    status === 'stale' ? 'pill-warn' :
    'pill-muted';
  const statusLabel = status === 'stale' ? 'stale' : status;

  return (
    <Marker
      position={[geo.lat, geo.lon]}
      icon={icon}
      ref={(m) => registerRef?.(device.thing, m)}
    >
      <Popup>
        <div className="text-xs space-y-1.5 min-w-[220px]">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-fg">
              {device.friendly_name ?? (
                <span className="font-mono">{device.thing}</span>
              )}
            </span>
            <span className={statusPillClass}>{statusLabel}</span>
            {group && <span className="pill-muted">{ENV_GROUP_LABEL[group]}</span>}
            <span className="pill-muted">{device.tenant_id}</span>
          </div>
          {device.friendly_name && (
            <div className="font-mono text-muted text-[10px]">
              {device.thing}
            </div>
          )}
          <div className="flex items-baseline gap-2 py-0.5">
            <span className="text-muted w-20 shrink-0">Position</span>
            <span className="tabular-nums">
              {geo.lat.toFixed(5)}, {geo.lon.toFixed(5)}
              {geo.accuracy ? ` ±${Math.round(geo.accuracy)} m` : ''}
            </span>
          </div>
          {geo.source && (
            <div className="flex items-baseline gap-2 py-0.5">
              <span className="text-muted w-20 shrink-0">Source</span>
              <span>{geo.source}</span>
            </div>
          )}
          <div className="flex items-baseline gap-2 py-0.5">
            <span className="text-muted w-20 shrink-0">Last seen</span>
            <span className="tabular-nums">{fmtAgo(device.last_seen)}</span>
          </div>
          <div className="flex gap-3 pt-1.5 border-t border-border">
            <Link
              to={`/devices/${encodeURIComponent(device.thing)}`}
              className="text-accent hover:underline"
            >
              Open detail →
            </Link>
            <Link
              to={`/devices/${encodeURIComponent(device.thing)}/locations`}
              className="text-accent hover:underline"
            >
              History →
            </Link>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}
